import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  FormHelperText,
  styled,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ArrowDropDown } from '@mui/icons-material';
import useAdd from '../../api/useAdd';
import useFetchObjects from '../../api/useFetchObjects';
import { useAuth } from '../../context/AuthContext';

// RTL-aware TextField component with improved styling
const RTLTextField = styled(TextField)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      padding: '10px 14px',
    },
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiOutlinedInput-root': {
      paddingLeft: 14,
      paddingRight: 0,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'right',
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

// RTL-aware FormControl with improved dropdown styling
const RTLFormControl = styled(FormControl)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
    },
    '& .MuiSelect-select': {
      paddingRight: '32px',
      textAlign: 'right',
      paddingLeft: '32px',
    },
    '& .MuiSelect-icon': {
      right: 'auto',
      left: 7,
      pointerEvents: 'none',
      color: theme.palette.action.active,
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

// RTL-aware Button component
const RTLButton = styled(Button)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
}));

const DeputyMinistryCreate = () => {
  const { t, i18n } = useTranslation(['deputy-ministries', 'common', 'validation']);
  const { user } = useAuth();
  const token = user?.token;
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ministry_id: '',
  });

  // Validation state
  const [errors, setErrors] = useState({});

  // Add mutation hook
  const { 
    handleAdd, 
    loading, 
    success 
  } = useAdd(
    'deputy-ministries',
    token,
    '/deputy-ministries',
    t('create_success'),
    t('create_error')
  );
  // Fetch ministries for dropdown
  const {
    data: ministriesData,
    isLoading: isLoadingMinistries,
    isError: isErrorMinistries,
  } = useFetchObjects('ministries', {}, token);

  const ministries = Array.isArray(ministriesData) ? ministriesData : (ministriesData?.data || []);

  // Reset form when creation is successful
  useEffect(() => {
    if (success) {
      setFormData({
        name: '',
        description: '',
      });
      setErrors({});
    }
  }, [success]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation:required');
    }

    if (!formData.ministry_id) {
      newErrors.ministry_id = t('validation:required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Create a copy of the form data
    const submissionData = { ...formData };
    
    // Submit form data
    handleAdd(submissionData);
  };

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit} 
      dir={isRtl ? 'rtl' : 'ltr'}
      sx={{ textAlign: isRtl ? 'right' : 'left', width: '100%' }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 3, 
        flexDirection: isRtl ? 'row-reverse' : 'row',
        width: '100%'
      }}>
        <RTLButton
          component={RouterLink}
          to="/deputy-ministries"
          startIcon={!isRtl && <ArrowBackIcon />}
          endIcon={isRtl && <ArrowBackIcon sx={{ transform: 'scaleX(-1)' }} />}
          sx={{ 
            mr: isRtl ? 0 : 2, 
            ml: isRtl ? 2 : 0,
            whiteSpace: 'nowrap'
          }}
          isRtl={isRtl}
        >
          {t('common:back')}
        </RTLButton>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            flexGrow: 1,
            textAlign: isRtl ? 'right' : 'left',
            fontWeight: 'bold',
          }}
        >
          {t('create_title')}
        </Typography>
      </Box>

      <Card sx={{ mb: 4, width: '100%' }}>
        <CardContent>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              textAlign: isRtl ? 'right' : 'left',
              fontWeight: 600,
            }}
          >
            {t('basic_info')}
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <RTLTextField
                fullWidth
                name="name"
                label={t('name')}
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                required
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                FormHelperTextProps={{
                  style: { textAlign: isRtl ? 'right' : 'left' }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <RTLTextField
                fullWidth
                name="description"
                label={t('description')}
                value={formData.description || ''}
                onChange={handleChange}
                multiline
                rows={3}
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                FormHelperTextProps={{
                  style: { textAlign: isRtl ? 'right' : 'left' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RTLFormControl fullWidth error={!!errors.ministry_id} required isRtl={isRtl}>
                <InputLabel id="ministry-label">{t('ministry')}</InputLabel>
                <Select
                  labelId="ministry-label"
                  name="ministry_id"
                  label={t('ministry')}
                  value={formData.ministry_id}
                  onChange={handleChange}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: isRtl ? 'right' : 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: isRtl ? 'right' : 'left',
                    },
                    PaperProps: {
                      style: {
                        direction: isRtl ? 'rtl' : 'ltr',
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>{t('select')}</em>
                  </MenuItem>
                  {isLoadingMinistries ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> {t('common:loading')}
                    </MenuItem>
                  ) : (
                    ministries.map((ministry) => (
                      <MenuItem key={ministry.id} value={ministry.id}>
                        {ministry.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.ministry_id && (
                  <FormHelperText>{errors.ministry_id}</FormHelperText>
                )}
              </RTLFormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      <Box sx={{ 
        display: 'flex', 
        justifyContent: isRtl ? 'flex-start' : 'flex-end', 
        gap: 2,
        width: '100%'
      }}>
        <RTLButton
          variant="outlined"
          component={RouterLink}
          to="/deputy-ministries"
          isRtl={isRtl}
        >
          {t('common:cancel')}
        </RTLButton>
        <RTLButton
          variant="contained"
          type="submit"
          disabled={loading}
          isRtl={isRtl}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            t('common:save')
          )}
        </RTLButton>
      </Box>
    </Box>
  );
};

export default DeputyMinistryCreate; 